import { Label } from "layout/typography/Label";
import { sortBy } from "lodash";
import {
  KeywordChips,
  withPending,
} from "pages/HospitalsPage/components/BenefitSelection/KeywordChips";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { useEffect, useMemo, useState } from "react";
import {
  useGetLineVocabularySuggestionsQuery,
  usePreviewLineVocabularyMutation,
} from "store/queries/benefitLineVocabulary";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";
import {
  IVocabularyPreviewView,
  IVocabularyRow,
  IVocabularySaveRequest,
} from "types/BenefitSelection/BenefitSelection";

const SuggestionChip = styled.button`
  border: 1px solid var(--surface-300);
  background: var(--surface-50);
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.8rem;
  cursor: pointer;

  &:hover {
    border-color: var(--primary-color);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const NEW_TYPE = "__new__";

/** The service lines a suggestion or a preview may be scoped to — `SERVICE_LINES` on the API. */
export const SUGGESTION_SERVICE_LINES = [
  { label: "Surgical", value: "SURGICAL" },
  { label: "Ambulatory surgery center", value: "ASC" },
  { label: "Hospital outpatient", value: "HOSPITAL_OUTPATIENT" },
  { label: "Hospital inpatient", value: "HOSPITAL_INPATIENT" },
  { label: "Physician office", value: "SPECIALIST_OFFICE" },
  { label: "MRI/CAT Scan", value: "MRI_CT" },
];

export const PLACE_OF_SERVICE_CHOICES = [
  { label: "Any place of service", value: "" },
  { label: "Outpatient Hospital", value: "Outpatient Hospital" },
  { label: "Ambulatory Surgical Center", value: "Ambulatory Surgical Center" },
  { label: "Inpatient Hospital", value: "Inpatient Hospital" },
  { label: "Office", value: "Office" },
];

const toDraft = (row: IVocabularyRow | null): IVocabularySaveRequest => ({
  role: row?.role ?? "",
  payerId: row?.payerId ?? null,
  matchKeywords: row?.matchKeywords ?? [],
  excludeKeywords: row?.excludeKeywords ?? [],
  placeOfService: row?.placeOfService ?? null,
  reason: row?.reason ?? "",
  effectiveFrom: row?.effectiveFrom ?? null,
  effectiveTo: row?.effectiveTo ?? null,
});

interface ILineTypeDialogProps {
  visible: boolean;
  /** The definition being edited, or null when adding. */
  row: IVocabularyRow | null;
  /** Line types already in use anywhere, so a definition attaches to an existing one by default. */
  knownRoles: string[];
  saving: boolean;
  onSave: (body: IVocabularySaveRequest) => void;
  onHide: () => void;
}

/**
 * CLE-2792 — one LINE TYPE DEFINITION: how a payer identifies a kind of benefit line.
 *
 * 🚨 "APPLIES TO" IS A REAL FORK AND IT WRITES DIFFERENT ROWS. "Every payer" writes one row that
 * every payer without one of its own falls back to; "Specific payers" writes ONE ROW PER PAYER, so
 * the same wording cannot drift apart payer by payer through re-typing.
 *
 * ⚠️ AN EDIT CANNOT RE-SCOPE. A saved row belongs to exactly one scope; changing it would mean
 * deleting that row and writing others, which is not what "Save" reads as. The scope is shown and
 * is not editable — remove the row and add it again to move it. It is still SENT on every edit,
 * because the PUT re-scopes to every payer when it is absent rather than leaving it alone.
 *
 * 🚨 THE SUGGESTED WORDING IS REAL DATA AND IS THE POINT OF THE DIALOG: the most frequent message
 * values on that payer's own lines, read from stored responses, no eligibility call. It is what
 * stops an editor guessing a payer's wording — which is how a definition gets saved that matches
 * nothing and still looks saved. It needs ONE payer to be about, so it appears when exactly one is
 * chosen rather than showing one payer's wording beside a list of several.
 */
export function LineTypeDialog({
  visible,
  row,
  knownRoles,
  saving,
  onSave,
  onHide,
}: ILineTypeDialogProps) {
  const [draft, setDraft] = useState<IVocabularySaveRequest>(toDraft(row));
  const [typingNewType, setTypingNewType] = useState(false);
  const [scope, setScope] = useState<"all" | "payers">("all");
  const [payerIds, setPayerIds] = useState<number[]>([]);
  const [serviceLine, setServiceLine] = useState("SURGICAL");
  /** Keyword text typed and not yet a chip — see `KeywordChips`. Never silently discarded. */
  const [pending, setPending] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<IVocabularyPreviewView | undefined>(
    undefined
  );

  const [runPreview, { isLoading: previewing }] =
    usePreviewLineVocabularyMutation();
  const { data: payersData } = useGetAllPayersQuery(undefined, {
    skip: !visible,
  });

  const payerOptions = useMemo(
    () =>
      sortBy(
        ((payersData as any)?.data || payersData || []).map((payer: any) => ({
          label: payer.name,
          value: payer.id,
        })),
        ["label"]
      ),
    [payersData]
  );

  useEffect(() => {
    if (!visible) return;
    const next = toDraft(row);
    setDraft(next);
    setTypingNewType(Boolean(next.role) && !knownRoles.includes(next.role));
    setScope(row && row.payerId !== null ? "payers" : "all");
    setPayerIds(row?.payerId != null ? [row.payerId] : []);
    setPreview(undefined);
    setPending({});
    // `knownRoles` is DELIBERATELY not a dependency: it is a fresh array on every parent render, so
    // keying the effect on it would reset the form under the editor's hands mid-edit. It is read
    // once, when the dialog opens, only to decide whether the line type is an existing one.
  }, [visible, row]);

  /** One payer, and only one, is what a suggestion or a preview can be about. */
  const singlePayerId =
    scope === "payers" && payerIds.length === 1 ? payerIds[0] : null;

  const { data: suggestions, isFetching: loadingSuggestions } =
    useGetLineVocabularySuggestionsQuery(
      { payerId: singlePayerId ?? 0, serviceLine },
      { skip: !visible || singlePayerId === null }
    );

  const set = (patch: Partial<IVocabularySaveRequest>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const addKeyword = (keyword: string) => {
    if (draft.matchKeywords.includes(keyword)) return;
    set({ matchKeywords: [...draft.matchKeywords, keyword] });
  };

  /** The definition as it really stands, including keyword text still sitting in an input. */
  const committed: IVocabularySaveRequest = {
    ...draft,
    matchKeywords: withPending(draft.matchKeywords, pending.matchKeywords),
    excludeKeywords: withPending(
      draft.excludeKeywords,
      pending.excludeKeywords
    ),
  };

  const scopeStated = scope === "all" || payerIds.length > 0;

  const canSave =
    committed.role.trim().length > 0 &&
    committed.matchKeywords.length > 0 &&
    committed.matchKeywords.every((keyword) => keyword.trim().length > 0) &&
    committed.reason.trim().length > 0 &&
    scopeStated;

  const handlePreview = async () => {
    try {
      const result = await runPreview({
        ...committed,
        payerId: singlePayerId,
      }).unwrap();
      setPreview(result.data);
    } catch {
      setPreview(undefined);
    }
  };

  const handleSave = () => {
    // Save what is on screen, not what has been committed to chips — see `KeywordChips`.
    const draftToSave = committed;
    // 🚨 `payerIds` GOES ON EVERY REQUEST, INCLUDING AN EDIT THAT DID NOT TOUCH THE SCOPE. The PUT
    // reads an absent `payerIds` as "re-scope this row to every payer", so leaving it off would
    // widen one payer's definition to all of them — a money change on every account naming the
    // line type, made by somebody who came to fix a typo in a keyword.
    if (row) {
      onSave({
        ...draftToSave,
        payerIds: row.payerId === null ? null : [row.payerId],
      });
      return;
    }
    onSave({ ...draftToSave, payerIds: scope === "all" ? null : payerIds });
  };

  // 🚨 THE RAW VALUE IS THE NAME — see `LineSelectionDialog`. One spelling, everywhere.
  const typeOptions = [
    ...knownRoles.map((role) => ({ label: role, value: role })),
    { label: "New line type…", value: NEW_TYPE },
  ];

  return (
    <Dialog
      header={row ? "Edit line type definition" : "Add a line type definition"}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(50rem, 95vw)" }}
      data-testid="lineTypeDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Cancel"
            className="p-button-text p-button-sm"
            data-testid="lineTypeDialog.cancel"
            onClick={onHide}
          />
          <Button
            className="p-button-sm"
            type="button"
            label={row ? "Save definition" : "Add definition"}
            loading={saving}
            disabled={!canSave}
            data-testid="lineTypeDialog.save"
            onClick={handleSave}
          />
        </div>
      }
    >
      <Surface className="flex flex-column gap-3">
        {row ? (
          <span
            className="text-sm text-color-secondary"
            data-testid="lineTypeDialog.scopeFixed"
          >
            Applies to{" "}
            {row.payerId === null
              ? "every payer"
              : row.payerName ?? `payer #${row.payerId}`}
            . Remove this definition and add it again to change that.
          </span>
        ) : (
          <div className="flex flex-column gap-2">
            <Label htmlFor="lineTypeDialog.scope">Applies to</Label>
            <div className="flex gap-4">
              <span className="flex align-items-center gap-2">
                <RadioButton
                  inputId="lineTypeScopeAll"
                  checked={scope === "all"}
                  onChange={() => setScope("all")}
                />
                <label htmlFor="lineTypeScopeAll" className="text-sm">
                  Every payer
                </label>
              </span>
              <span className="flex align-items-center gap-2">
                <RadioButton
                  inputId="lineTypeScopePayers"
                  checked={scope === "payers"}
                  onChange={() => setScope("payers")}
                />
                <label htmlFor="lineTypeScopePayers" className="text-sm">
                  Specific payers
                </label>
              </span>
            </div>
            {scope === "payers" && (
              <MultiSelect
                data-testid="lineTypeDialog.payerIds"
                className="w-full"
                display="chip"
                filter
                options={payerOptions}
                value={payerIds}
                placeholder="Choose payers"
                onChange={(e) => setPayerIds(e.value ?? [])}
              />
            )}
            {scope === "payers" && (
              <small className="text-color-secondary">
                One definition is written per payer, so the wording cannot drift
                apart payer by payer later.
              </small>
            )}
          </div>
        )}

        <div className="formgrid grid">
          <div className="field col-12 md:col-6">
            <Label htmlFor="lineTypeDialog.role">Line type</Label>
            {typingNewType ? (
              <InputText
                id="lineTypeDialog.role"
                data-testid="lineTypeDialog.newRole"
                className="w-full"
                placeholder="PHYSICIAN_LINE"
                value={draft.role}
                onChange={(e) => set({ role: e.target.value })}
              />
            ) : (
              <Dropdown
                id="lineTypeDialog.role"
                data-testid="lineTypeDialog.role"
                className="w-full"
                options={typeOptions}
                placeholder="Choose a line type"
                value={draft.role}
                onChange={(e) => {
                  if (e.value === NEW_TYPE) {
                    setTypingNewType(true);
                    set({ role: "" });
                    return;
                  }
                  set({ role: e.value });
                }}
              />
            )}
            <small className="text-color-secondary">
              Stored as UPPER_SNAKE, so one line type cannot become several.
            </small>
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="lineTypeDialog.placeOfService">
              Place of service (optional)
            </Label>
            <Dropdown
              id="lineTypeDialog.placeOfService"
              data-testid="lineTypeDialog.placeOfService"
              className="w-full"
              options={PLACE_OF_SERVICE_CHOICES}
              value={draft.placeOfService ?? ""}
              onChange={(e) => set({ placeOfService: e.value || null })}
            />
          </div>

          <div className="field col-12">
            <Label htmlFor="lineTypeDialog.matchKeywords">
              Line message contains any of
            </Label>
            <KeywordChips
              id="lineTypeDialog.matchKeywords"
              testId="lineTypeDialog.matchKeywords"
              value={draft.matchKeywords}
              placeholder="OUTPATIENT SURGEON"
              onChange={(values, text) => {
                set({ matchKeywords: values });
                setPending((current) => ({ ...current, matchKeywords: text }));
              }}
            />
            <small className="text-color-secondary">
              Whole words, case-insensitive, per message element. A blank
              keyword is rejected rather than dropped — a blank matches nothing,
              so dropping it would make a typo look like a saved rule.
            </small>
          </div>

          {singlePayerId !== null && (
            <div className="field col-12">
              <div className="flex align-items-center gap-2 mb-2">
                <span className="font-medium text-sm">
                  Wording this payer actually sends
                </span>
                <Dropdown
                  data-testid="lineTypeDialog.serviceLine"
                  options={SUGGESTION_SERVICE_LINES}
                  value={serviceLine}
                  onChange={(e) => setServiceLine(e.value)}
                />
              </div>
              {loadingSuggestions && (
                <span className="text-sm text-color-secondary">Loading…</span>
              )}
              {!loadingSuggestions && suggestions?.data && (
                <>
                  <div
                    className="flex flex-wrap gap-2"
                    data-testid="lineTypeDialog.suggestions"
                  >
                    {suggestions.data.suggestions.map((suggestion) => (
                      <SuggestionChip
                        key={suggestion.message}
                        type="button"
                        disabled={draft.matchKeywords.includes(
                          suggestion.message
                        )}
                        data-testid={`lineTypeDialog.suggestion.${suggestion.message}`}
                        onClick={() => addKeyword(suggestion.message)}
                      >
                        {suggestion.message} · {suggestion.lines} lines
                      </SuggestionChip>
                    ))}
                  </div>
                  <small className="text-color-secondary">
                    The most frequent message values on this payer&apos;s lines
                    for that service line, over the last{" "}
                    {suggestions.data.windowDays} days — read from stored
                    responses, no eligibility call. Click one to add it.
                    Narrowing to a payer is approximate: it matches through the
                    payer&apos;s name and aliases (
                    {suggestions.data.matchedPayerNames.join(", ") || "none"}).
                  </small>
                </>
              )}
            </div>
          )}

          <div className="field col-12">
            <Label htmlFor="lineTypeDialog.excludeKeywords">
              Does not contain — a line containing any of these is never this
              line type
            </Label>
            <KeywordChips
              id="lineTypeDialog.excludeKeywords"
              testId="lineTypeDialog.excludeKeywords"
              value={draft.excludeKeywords}
              placeholder="ASSISTANT"
              onChange={(values, text) => {
                set({ excludeKeywords: values });
                setPending((current) => ({
                  ...current,
                  excludeKeywords: text,
                }));
              }}
            />
          </div>

          <div className="field col-12">
            <Label htmlFor="lineTypeDialog.reason">
              Why — one sentence, shown wherever this definition is listed
            </Label>
            <InputTextarea
              id="lineTypeDialog.reason"
              data-testid="lineTypeDialog.reason"
              className="w-full"
              rows={2}
              value={draft.reason}
              onChange={(e) => set({ reason: e.target.value })}
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="lineTypeDialog.effectiveFrom">
              Effective from (optional)
            </Label>
            <Calendar
              id="lineTypeDialog.effectiveFrom"
              data-testid="lineTypeDialog.effectiveFrom"
              className="w-full"
              value={draft.effectiveFrom ? new Date(draft.effectiveFrom) : null}
              onChange={(e) =>
                set({
                  effectiveFrom: e.value
                    ? (e.value as Date).toISOString()
                    : null,
                })
              }
            />
          </div>

          <div className="field col-12 md:col-6">
            <Label htmlFor="lineTypeDialog.effectiveTo">
              Effective to (optional)
            </Label>
            <Calendar
              id="lineTypeDialog.effectiveTo"
              data-testid="lineTypeDialog.effectiveTo"
              className="w-full"
              value={draft.effectiveTo ? new Date(draft.effectiveTo) : null}
              onChange={(e) =>
                set({
                  effectiveTo: e.value ? (e.value as Date).toISOString() : null,
                })
              }
            />
          </div>
        </div>

        {singlePayerId !== null && (
          <div className="flex flex-column gap-2">
            <div>
              <Button
                type="button"
                label="Check what this would match"
                className="p-button-outlined p-button-sm"
                loading={previewing}
                disabled={committed.matchKeywords.length === 0}
                data-testid="lineTypeDialog.preview"
                onClick={handlePreview}
              />
            </div>
            {preview && (
              <span
                className="text-sm text-color-secondary"
                data-testid="lineTypeDialog.previewResult"
              >
                {`Would match ${preview.linesMatched} lines on ${preview.responsesMatched} of this payer's last ${preview.observationsScanned} stored checks in ${preview.windowDays} days. ${preview.accountsNamingRole.length} account selector(s) name ${preview.role}.`}
              </span>
            )}
          </div>
        )}

        {!canSave && (
          <span
            className="text-sm text-color-secondary"
            data-testid="lineTypeDialog.incomplete"
          >
            A definition needs a line type, at least one keyword, a one-sentence
            reason, and — for &quot;Specific payers&quot; — at least one payer.
          </span>
        )}
      </Surface>
    </Dialog>
  );
}
